import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '../ui/label';
import { Textarea } from '@/components/ui/Textarea';
import { Checkbox } from '../ui/checkbox';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { CalendarIcon, Save, Send, AlertCircle, CheckCircle2, Info } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { getMyAllReports } from '@/api/workforce';
import { Employee } from '@/types/workforce';
import { RecipientSelector } from './feedback/RecipientSelector';
import { mapFormDataToQuickFeedback } from '@/api/performance';
import { useAuth } from '@/hooks/useAuth';
import { useErrorToast } from '@/hooks/useErrorToast';

// 🚀 NEW: TanStack Query mutation hook
import { useCreateQuickFeedback } from '@/hooks/queries/useFeedbackQueries';

interface FormData {
  tipo: string;
  categorias: string[];
  destinatario: {
    nome: string;
    cargo: string;
    area: string;
  };
  contexto: {
    projeto: string;
    data: string;
    linkWrike: string;
  };
  observacao: string;
  impacto: {
    tipos: string[];
    detalhamento: string;
  };
  expectativaPadrao: string;
  acordo: {
    acao: string;
    responsavel: string;
    prazo: string;
  };
  suporteNecessario: string;
  followUpDate: Date | null;
  confidencialidade: string;
  compartilharCom: string;
}

interface ValidationErrors {
  tipo?: string;
  destinatario?: string;
  observacao?: string;
}

interface FeedbackRapidoProps {
  onSuccess?: () => void;
}

const FeedbackRapido = ({ onSuccess }: FeedbackRapidoProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError, showWarning } = useErrorToast();

  // 🔄 TanStack Query mutation for creating feedback
  const createFeedbackMutation = useCreateQuickFeedback();

  // Direct Reports State
  const [directReports, setDirectReports] = useState<Employee[]>([]);
  const [loadingReports, setLoadingReports] = useState(true);
  const [selectedRecipient, setSelectedRecipient] = useState<Employee | null>(null);

  // Submission State (no longer need submitting state - use mutation.isPending)
  const [lastSavedFeedbackId, setLastSavedFeedbackId] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Validation State
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState<Set<string>>(new Set());

  // Auto-save State
  const [lastAutoSave, setLastAutoSave] = useState<Date | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Form State
  const [formData, setFormData] = useState<FormData>({
    tipo: '',
    categorias: [],
    destinatario: {
      nome: '',
      cargo: '',
      area: ''
    },
    contexto: {
      projeto: '',
      data: '',
      linkWrike: ''
    },
    observacao: '',
    impacto: {
      tipos: [],
      detalhamento: ''
    },
    expectativaPadrao: '',
    acordo: {
      acao: '',
      responsavel: '',
      prazo: ''
    },
    suporteNecessario: '',
    followUpDate: null,
    confidencialidade: 'privado',
    compartilharCom: ''
  });

  // Load direct reports on mount
  useEffect(() => {
    const fetchDirectReports = async () => {
      try {
        setLoadingReports(true);
        const response = await getMyAllReports({ limit: 1000 });
        setDirectReports(response.items);
      } catch (error) {
        console.error('Failed to load direct reports:', error);
      } finally {
        setLoadingReports(false);
      }
    };

    fetchDirectReports();
  }, []);

  // Auto-save effect (every 30 seconds)
  useEffect(() => {
    if (!hasUnsavedChanges || !formData.tipo || !formData.observacao) {
      return;
    }

    const autoSaveTimer = setTimeout(() => {
      handleAutoSave();
    }, 30000); // 30 seconds

    return () => clearTimeout(autoSaveTimer);
  }, [formData, hasUnsavedChanges]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + S for save
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        handleSalvarFeedback();
      }
      // Cmd/Ctrl + Enter for send
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        setShowConfirmDialog(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [formData]);

  const tiposFeedback = [
    { id: 'reconhecimento', label: 'Reconhecimento', description: 'Valorizar boas práticas e resultados', icon: '' },
    { id: 'construtivo', label: 'Construtivo', description: 'Sugerir melhorias e desenvolvimento', icon: '' },
    { id: 'critico', label: 'Crítico', description: 'Abordar questões que precisam de atenção imediata', icon: '' }
  ];

  const categorias = [
    'Técnico', 'Comportamental', 'Comunicação', 'Processos & Wrike',
    'Projetos', 'Resultados & Finanças', 'Negócios & Cliente',
    'Diversidade & Inclusão', 'Conflitos & Ética', 'Fit Cultural'
  ];

  const tiposImpacto = [
    'Cliente', 'Prazo', 'Custo', 'Qualidade', 'Clima/Equipe'
  ];

  // Validation function
  const validateForm = useCallback((): ValidationErrors => {
    const newErrors: ValidationErrors = {};

    if (!formData.tipo) {
      newErrors.tipo = 'Selecione o tipo de feedback';
    }

    const hasRecipient = selectedRecipient !== null;
    if (!hasRecipient) {
      newErrors.destinatario = 'Selecione um destinatário';
    }

    if (!formData.observacao || formData.observacao.trim().length < 10) {
      newErrors.observacao = 'A observação deve ter pelo menos 10 caracteres';
    }

    return newErrors;
  }, [formData, selectedRecipient]);

  // Real-time validation
  useEffect(() => {
    if (touched.size > 0) {
      const newErrors = validateForm();
      setErrors(newErrors);
    }
  }, [formData, selectedRecipient, validateForm, touched]);

  // Track unsaved changes
  useEffect(() => {
    const hasData = Boolean(formData.tipo || formData.observacao || formData.categorias.length > 0);
    setHasUnsavedChanges(hasData);
  }, [formData]);

  // Recipient Selection Handler
  const handleSelectRecipient = (employee: Employee | null) => {
    setSelectedRecipient(employee);
    setTouched(prev => new Set(prev).add('destinatario'));

    if (employee) {
      // Auto-populate form with employee data
      setFormData({
        ...formData,
        destinatario: {
          nome: employee.personalInfo?.name || '',
          cargo: employee.currentEngagement?.roleName || '',
          area: employee.currentEngagement?.departmentName || ''
        }
      });
    }
  };

  const handleCategoriaChange = (categoria: string, checked: boolean | string) => {
    const isChecked = checked === true;
    if (isChecked) {
      setFormData({
        ...formData,
        categorias: [...formData.categorias, categoria]
      });
    } else {
      setFormData({
        ...formData,
        categorias: formData.categorias.filter(c => c !== categoria)
      });
    }
  };

  const handleImpactoChange = (tipo: string, checked: boolean | string) => {
    const isChecked = checked === true;
    if (isChecked) {
      setFormData({
        ...formData,
        impacto: {
          ...formData.impacto,
          tipos: [...formData.impacto.tipos, tipo]
        }
      });
    } else {
      setFormData({
        ...formData,
        impacto: {
          ...formData.impacto,
          tipos: formData.impacto.tipos.filter(t => t !== tipo)
        }
      });
    }
  };

  const handleAutoSave = async () => {
    try {
      const feedbackDataRaw = mapFormDataToQuickFeedback(formData, selectedRecipient);

      // ✅ Map to CreateFeedbackRequest for the mutation hook
      const feedbackData = {
        type: 'quick' as const,
        ...feedbackDataRaw,
        status: 'draft' as const,
      };

      // ✅ Use TanStack Query mutation
      const savedFeedback = await createFeedbackMutation.mutateAsync(feedbackData);

      setLastSavedFeedbackId(savedFeedback.feedbackId);
      setLastAutoSave(new Date());
      setHasUnsavedChanges(false);

      showSuccess('Rascunho salvo automaticamente', {
        description: format(new Date(), "HH:mm:ss", { locale: ptBR })
      });
    } catch (error) {
      console.error('❌ Erro ao salvar automaticamente:', error);
      // Error toast already handled by mutation hook
    }
  };

  const handleSalvarFeedback = async () => {
    // Mark all fields as touched for validation
    setTouched(new Set(['tipo', 'destinatario', 'observacao']));

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      showWarning('Por favor, corrija os campos obrigatórios', {
        description: 'Verifique o tipo, destinatário e observação'
      });
      return;
    }

    try {
      // Map form data to backend structure
      const feedbackDataRaw = mapFormDataToQuickFeedback(formData, selectedRecipient);

      // ✅ Map to CreateFeedbackRequest for the mutation hook
      const feedbackData = {
        type: 'quick' as const,
        ...feedbackDataRaw,
        status: 'draft' as const,
      };

      // ✅ Use TanStack Query mutation (handles loading state automatically)
      const savedFeedback = await createFeedbackMutation.mutateAsync(feedbackData);

      setLastSavedFeedbackId(savedFeedback.feedbackId);
      setHasUnsavedChanges(false);

      showSuccess('Feedback salvo como rascunho!', {
        description: `ID: ${savedFeedback.feedbackId}`
      });
    } catch (error) {
      console.error('❌ Erro ao salvar feedback:', error);
      showError(error);
    }
  };

  const handleEnviarFeedback = async () => {
    // Validate before showing confirmation
    setTouched(new Set(['tipo', 'destinatario', 'observacao']));

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      showWarning('Por favor, corrija os campos obrigatórios', {
        description: 'Verifique o tipo, destinatário e observação'
      });
      return;
    }

    setShowConfirmDialog(false);

    try {
      // Map form data to backend structure
      const feedbackDataRaw = mapFormDataToQuickFeedback(formData, selectedRecipient);

      // ✅ Map to CreateFeedbackRequest for the mutation hook
      const feedbackData = {
        type: 'quick' as const,
        ...feedbackDataRaw,
        status: 'delivered' as const,
      };

      // ✅ Use TanStack Query mutation
      await createFeedbackMutation.mutateAsync(feedbackData);

      // Close the confirmation dialog
      setShowConfirmDialog(false);

      showSuccess('Feedback registrado com sucesso!');

      // Handle success - either callback (when inside FeedbackHub) or navigate (when standalone)
      if (onSuccess) {
        // Used within FeedbackHub - call callback to switch tab
        onSuccess();
      } else {
        // Standalone page - navigate to main feedbacks page
        setTimeout(() => {
          const targetPath = '/performance/feedbacks' as const;
          // @ts-ignore - TanStack Router typing issue
          navigate({ to: targetPath });
        }, 100);
      }
    } catch (error) {
      console.error('❌ Erro ao enviar feedback:', error);
      showError(error);
    }
  };

  return (
    <div className="space-y-6 pb-24">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-white text-2xl font-display font-bold">Feedback Rápido</h2>
          <p className="text-brand-gray font-body">Formato enxuto para situações pontuais e conversas 1:1</p>
        </div>
        {lastAutoSave && (
          <div className="flex items-center gap-2 text-sm text-brand-gray">
            <CheckCircle2 className="h-4 w-4 text-success" />
            <span>Salvo às {format(lastAutoSave, "HH:mm", { locale: ptBR })}</span>
          </div>
        )}
      </div>

      {/* Keyboard Shortcuts Info */}
      <Card>
        <CardContent className="py-3">
          <div className="flex items-center gap-4 text-sm text-brand-gray">
            <Info className="h-4 w-4" />
            <span><kbd className="px-2 py-1 rounded bg-brand-dark text-xs">⌘S</kbd> para salvar</span>
            <span><kbd className="px-2 py-1 rounded bg-brand-dark text-xs">⌘Enter</kbd> para registrar</span>
          </div>
        </CardContent>
      </Card>

      {/* Destinatário - REQUIRED (FIRST) */}
      <div>
        <RecipientSelector
          directReports={directReports}
          loading={loadingReports}
          selectedRecipient={selectedRecipient}
          onSelectRecipient={handleSelectRecipient}
        />
        {touched.has('destinatario') && errors.destinatario && (
          <p className="text-error text-sm mt-2">{errors.destinatario}</p>
        )}
      </div>

      {/* Tipo de Feedback - REQUIRED */}
      <Card>
        <CardHeader>
          <CardTitle className="text-white">
            Tipo de Feedback <span className="text-error">*</span>
          </CardTitle>
          <CardDescription>
            Escolha o tipo de feedback que melhor se adequa à situação
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {tiposFeedback.map((tipo) => (
            <label
              key={tipo.id}
              className={`flex items-start gap-3 p-4 rounded-monumenta border cursor-pointer transition-all ${
                formData.tipo === tipo.id
                  ? 'border-brand-primary bg-brand-primary/10'
                  : 'border-brand-gray/20 hover:border-brand-primary/50 hover:bg-brand-dark'
              }`}
              onClick={() => {
                setFormData({...formData, tipo: tipo.id});
                setTouched(prev => new Set(prev).add('tipo'));
              }}
            >
              <div className="flex items-center justify-center w-5 h-5 mt-0.5">
                {formData.tipo === tipo.id ? (
                  <div className="w-4 h-4 rounded-full border-2 border-brand-primary flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-brand-primary" />
                  </div>
                ) : (
                  <div className="w-4 h-4 rounded-full border-2 border-brand-gray" />
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xl">{tipo.icon}</span>
                  <span className="text-white font-medium">{tipo.label}</span>
                </div>
                <p className="text-brand-gray text-sm">{tipo.description}</p>
              </div>
            </label>
          ))}
          {touched.has('tipo') && errors.tipo && (
            <p className="text-error text-sm mt-2">{errors.tipo}</p>
          )}
        </CardContent>
      </Card>

      {/* Observação - REQUIRED */}
      <Card>
        <CardHeader>
          <CardTitle className="text-white">
            Observação <span className="text-error">*</span>
          </CardTitle>
          <CardDescription>
            Descreva o comportamento ou situação observada de forma objetiva e específica
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Textarea
              value={formData.observacao}
              onChange={(e) => {
                setFormData({...formData, observacao: e.target.value});
                setTouched(prev => new Set(prev).add('observacao'));
              }}
              className={`w-full min-h-32 ${touched.has('observacao') && errors.observacao ? 'border-error focus:ring-error focus:border-error' : ''}`}
              placeholder="Descreva o comportamento ou situação observada de forma objetiva e específica..."
            />
            <div className="flex justify-between items-center mt-2">
              {touched.has('observacao') && errors.observacao ? (
                <p className="text-error text-sm">{errors.observacao}</p>
              ) : (
                <p className="text-brand-gray text-sm">Mínimo de 10 caracteres</p>
              )}
              <span className={`text-sm ${formData.observacao.length < 10 ? 'text-brand-gray' : 'text-success'}`}>
                {formData.observacao.length} caracteres
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Categorias */}
      <Card>
        <CardHeader>
          <CardTitle className="text-white">Categoria(s)</CardTitle>
          <CardDescription>
            Selecione uma ou mais categorias relevantes (opcional)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {categorias.map((categoria) => (
              <label key={categoria} className="flex items-center gap-3 p-3 rounded-monumenta border border-brand-gray/20 hover:border-brand-primary/50 hover:bg-brand-dark transition-all cursor-pointer">
                <Checkbox
                  checked={formData.categorias.includes(categoria)}
                  onCheckedChange={(checked) => handleCategoriaChange(categoria, checked)}
                />
                <span className="text-sm text-white font-body">{categoria}</span>
              </label>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Contexto */}
      <Card>
        <CardHeader>
          <CardTitle className="text-white">Contexto</CardTitle>
          <CardDescription>
            Informações adicionais sobre o contexto do feedback
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="projeto">Projeto/Cliente</Label>
              <Input
                id="projeto"
                value={formData.contexto.projeto}
                onChange={(e) => setFormData({
                  ...formData,
                  contexto: { ...formData.contexto, projeto: e.target.value }
                })}
                placeholder="Nome do projeto ou cliente"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dataContexto">Data</Label>
              <Input
                id="dataContexto"
                type="date"
                value={formData.contexto.data}
                onChange={(e) => setFormData({
                  ...formData,
                  contexto: { ...formData.contexto, data: e.target.value }
                })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="linkWrike">Link Wrike/Teams</Label>
              <Input
                id="linkWrike"
                value={formData.contexto.linkWrike}
                onChange={(e) => setFormData({
                  ...formData,
                  contexto: { ...formData.contexto, linkWrike: e.target.value }
                })}
                placeholder="URL de referência"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Impacto */}
      <Card>
        <CardHeader>
          <CardTitle className="text-white">Impacto</CardTitle>
          <CardDescription>
            Descreva o impacto da situação observada
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Áreas impactadas:</Label>
            <div className="grid grid-cols-3 md:grid-cols-5 gap-3 mt-2">
              {tiposImpacto.map((tipo) => (
                <label key={tipo} className="flex items-center gap-2 p-2 rounded-monumenta border border-brand-gray/20 hover:border-brand-primary/50 hover:bg-brand-dark transition-all cursor-pointer">
                  <Checkbox
                    checked={formData.impacto.tipos.includes(tipo)}
                    onCheckedChange={(checked) => handleImpactoChange(tipo, checked)}
                  />
                  <span className="text-xs text-white font-body">{tipo}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="detalhamentoImpacto">Detalhamento do impacto:</Label>
            <Textarea
              id="detalhamentoImpacto"
              value={formData.impacto.detalhamento}
              onChange={(e) => setFormData({
                ...formData,
                impacto: { ...formData.impacto, detalhamento: e.target.value }
              })}
              placeholder="Explique como essa situação impacta o trabalho, equipe ou resultados..."
            />
          </div>
        </CardContent>
      </Card>

      {/* Expectativa/Padrão */}
      <Card>
        <CardHeader>
          <CardTitle className="text-white">Expectativa/Padrão</CardTitle>
          <CardDescription>
            Qual é o comportamento ou resultado esperado?
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Textarea
              value={formData.expectativaPadrao}
              onChange={(e) => setFormData({...formData, expectativaPadrao: e.target.value})}
              className="min-h-24"
              placeholder="Qual é o comportamento ou resultado esperado? Qual padrão deveria ser seguido?"
            />
          </div>
        </CardContent>
      </Card>

      {/* Acordo */}
      <Card>
        <CardHeader>
          <CardTitle className="text-white">Acordo</CardTitle>
          <CardDescription>
            Defina ações, responsáveis e prazos
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="acao">Ação</Label>
              <Input
                id="acao"
                value={formData.acordo.acao}
                onChange={(e) => setFormData({
                  ...formData,
                  acordo: { ...formData.acordo, acao: e.target.value }
                })}
                placeholder="O que será feito?"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="responsavel">Responsável</Label>
              <Input
                id="responsavel"
                value={formData.acordo.responsavel}
                onChange={(e) => setFormData({
                  ...formData,
                  acordo: { ...formData.acordo, responsavel: e.target.value }
                })}
                placeholder="Quem é responsável?"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="prazoAcordo">Prazo</Label>
              <Input
                id="prazoAcordo"
                type="date"
                value={formData.acordo.prazo}
                onChange={(e) => setFormData({
                  ...formData,
                  acordo: { ...formData.acordo, prazo: e.target.value }
                })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Suporte Necessário */}
      <Card>
        <CardHeader>
          <CardTitle className="text-white">Suporte Necessário</CardTitle>
          <CardDescription>
            Recursos, treinamentos ou apoio necessários
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Textarea
              value={formData.suporteNecessario}
              onChange={(e) => setFormData({...formData, suporteNecessario: e.target.value})}
              placeholder="Que recursos, treinamentos ou apoio são necessários para alcançar o objetivo?"
            />
          </div>
        </CardContent>
      </Card>

      {/* Follow-up */}
      <Card>
        <CardHeader>
          <CardTitle className="text-white">Follow-up</CardTitle>
          <CardDescription>
            Agende uma data para acompanhamento
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="btn-secondary"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {formData.followUpDate ? format(formData.followUpDate, "dd 'de' MMMM 'de' yyyy", { locale: ptBR }) : "Agendar follow-up"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-brand-dark-light border-brand-gray/20">
              <Calendar
                mode="single"
                selected={formData.followUpDate ?? undefined}
                onSelect={(date) => setFormData({...formData, followUpDate: date ?? null})}
                initialFocus
                className="bg-brand-dark-light text-white rounded-monumenta"
              />
            </PopoverContent>
          </Popover>
        </CardContent>
      </Card>

      {/* Confidencialidade */}
      <Card>
        <CardHeader>
          <CardTitle className="text-white">Confidencialidade</CardTitle>
          <CardDescription>
            Defina quem pode visualizar este feedback
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <label
              className={`flex items-center gap-3 p-3 rounded-monumenta border cursor-pointer transition-all ${
                formData.confidencialidade === 'privado'
                  ? 'border-brand-primary bg-brand-primary/10'
                  : 'border-brand-gray/20 hover:border-brand-primary/50 hover:bg-brand-dark'
              }`}
              onClick={() => setFormData({...formData, confidencialidade: 'privado'})}
            >
              <div className="flex items-center justify-center w-5 h-5">
                {formData.confidencialidade === 'privado' ? (
                  <div className="w-4 h-4 rounded-full border-2 border-brand-primary flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-brand-primary" />
                  </div>
                ) : (
                  <div className="w-4 h-4 rounded-full border-2 border-brand-gray" />
                )}
              </div>
              <span className="text-white text-sm flex-1">
                Privado (apenas você e o destinatário)
              </span>
            </label>

            <label
              className={`flex items-center gap-3 p-3 rounded-monumenta border cursor-pointer transition-all ${
                formData.confidencialidade === 'compartilhar'
                  ? 'border-brand-primary bg-brand-primary/10'
                  : 'border-brand-gray/20 hover:border-brand-primary/50 hover:bg-brand-dark'
              }`}
              onClick={() => setFormData({...formData, confidencialidade: 'compartilhar'})}
            >
              <div className="flex items-center justify-center w-5 h-5">
                {formData.confidencialidade === 'compartilhar' ? (
                  <div className="w-4 h-4 rounded-full border-2 border-brand-primary flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-brand-primary" />
                  </div>
                ) : (
                  <div className="w-4 h-4 rounded-full border-2 border-brand-gray" />
                )}
              </div>
              <span className="text-white text-sm flex-1">
                Compartilhar com outras pessoas
              </span>
            </label>
          </div>

          {formData.confidencialidade === 'compartilhar' && (
            <div className="space-y-2">
              <Label htmlFor="compartilharCom">Compartilhar com:</Label>
              <Input
                id="compartilharCom"
                value={formData.compartilharCom}
                onChange={(e) => setFormData({...formData, compartilharCom: e.target.value})}
                placeholder="Nome das pessoas que devem receber uma cópia"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Fixed Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-brand-dark-light border-t border-brand-gray/20 p-4 z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="text-sm text-brand-gray">
            {hasUnsavedChanges && (
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-warning animate-pulse" />
                Alterações não salvas
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleSalvarFeedback}
              disabled={createFeedbackMutation.isPending}
              className="btn-secondary"
            >
              <Save className="h-4 w-4 mr-2" />
              {createFeedbackMutation.isPending ? 'Salvando...' : 'Salvar Rascunho'}
            </Button>
            <Button
              onClick={() => setShowConfirmDialog(true)}
              disabled={createFeedbackMutation.isPending}
              className="btn-primary"
            >
              <Send className="h-4 w-4 mr-2" />
              {createFeedbackMutation.isPending ? 'Registrando...' : 'Registrar Feedback'}
            </Button>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="bg-brand-dark-light border-brand-gray/20">
          <DialogHeader>
            <DialogTitle className="text-white">Confirmar Registro de Feedback</DialogTitle>
            <DialogDescription className="text-brand-gray">
              Tem certeza que deseja registrar este feedback? Ele ficará disponível para consulta no sistema.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <div className="p-3 bg-brand-dark rounded-monumenta border border-brand-gray/20">
              <p className="text-sm text-brand-gray mb-1">Tipo:</p>
              <p className="text-white font-medium">
                {tiposFeedback.find(t => t.id === formData.tipo)?.label || '-'}
              </p>
            </div>
            <div className="p-3 bg-brand-dark rounded-monumenta border border-brand-gray/20">
              <p className="text-sm text-brand-gray mb-1">Destinatário:</p>
              <p className="text-white font-medium">
                {selectedRecipient?.personalInfo?.name || '-'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
              className="btn-secondary"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleEnviarFeedback}
              disabled={createFeedbackMutation.isPending}
              className="btn-primary"
            >
              <Send className="h-4 w-4 mr-2" />
              Confirmar Registro
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FeedbackRapido;
